/**
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema Golden Sisters
 * COPIAR ARQUIVO → src/components/pos/CartPane.js
 * Versão: 2.5.0 – Desconto Manual aplicado após pontos e total nunca negativo (UI consistente)
 * Data: 20/09/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - Remove a prévia “Aplicado: R$ …” no campo de desconto manual para evitar divergência visual.
 * - Em Totals, calcula o desconto manual sobre (subtotal - pointsDiscount), com clamp.
 * - Exibe o valor do desconto manual em R$ seguindo a mesma regra do POSPage.
 */

import React, { useState } from 'react';
import { Trash2, DollarSign, CreditCard, Smartphone, ShoppingCart, Loader2, Percent } from 'lucide-react';

// Sub-componente para os itens do carrinho
const CartItem = ({ item, onQuantityChange, onRemove, productStock }) => (
  <div className="flex items-center gap-3 bg-gray-800 p-2 rounded-md">
    <div className="flex-grow">
      <p className="text-sm font-semibold truncate">{item.name}</p>
      <p className="text-xs text-brand-gold">R$ {(item.price || 0).toFixed(2).replace('.', ',')}</p>
    </div>
    <input
      type="number"
      value={item.quantity}
      onChange={(e) => onQuantityChange(item.id, parseInt(e.target.value, 10))}
      className="w-16 bg-gray-900 text-center rounded-md p-1 border border-gray-700"
      min="1"
      max={productStock}
    />
    <button onClick={() => onRemove(item.id)} className="text-red-500 hover:text-red-400 p-1">
      <Trash2 size={16} />
    </button>
  </div>
);

// Campo de desconto manual em porcentagem (sem prévia em R$ para evitar inconsistências)
const ManualDiscount = ({ currentDiscountPercentage, onDiscountChange }) => {
  const [inputValue, setInputValue] = useState(
    currentDiscountPercentage || currentDiscountPercentage === 0
      ? String(currentDiscountPercentage)
      : ''
  );

  const handleInputChange = (e) => {
    let value = e.target.value;
    if (!/^\d*\.?\d*$/.test(value) && value !== '') return;

    const floatValue = parseFloat(value);
    if (floatValue > 100) value = '100';
    else if (floatValue < 0) value = '0';

    setInputValue(value);
    onDiscountChange(parseFloat(value) || 0);
  };

  React.useEffect(() => {
    setInputValue(
      currentDiscountPercentage || currentDiscountPercentage === 0
        ? String(currentDiscountPercentage)
        : ''
    );
  }, [currentDiscountPercentage]);

  return (
    <div className="mb-4 p-3 bg-gray-800 rounded-lg">
      <label htmlFor="manualDiscount" className="block text-sm font-medium text-gray-400 mb-1">
        Desconto Manual (%)
      </label>
      <div className="relative">
        <input
          type="text"
          id="manualDiscount"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="0"
          inputMode="decimal"
          className="w-full pl-3 pr-8 py-2 bg-gray-900 border-gray-700 rounded-lg text-white focus:ring-2 focus:outline-none focus:ring-brand-gold"
        />
        <Percent className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
      </div>
    </div>
  );
};

// Componente principal do Carrinho
const CartPane = ({
  cart,
  onUpdateCart,
  products,
  onManualDiscountPercentageChange,
  manualDiscountPercentage,
  subtotal,
}) => {
  const handleQuantityChange = (productId, newQuantity) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    const clampedQuantity = Math.max(1, Math.min(newQuantity || 1, product.stock || 1));
    const newCart = cart.map((item) =>
      item.id === productId ? { ...item, quantity: clampedQuantity } : item
    );
    onUpdateCart(newCart);
  };

  const handleRemoveFromCart = (productId) => {
    const newCart = cart.filter((item) => item.id !== productId);
    onUpdateCart(newCart);
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-4">Carrinho</h2>
      <div className="space-y-2">
        {cart.map((item) => (
          <CartItem
            key={item.id}
            item={item}
            onQuantityChange={handleQuantityChange}
            onRemove={handleRemoveFromCart}
            productStock={products.find((p) => p.id === item.id)?.stock || 0}
          />
        ))}
      </div>

      <CartPane.ManualDiscount
        currentDiscountPercentage={manualDiscountPercentage}
        onDiscountChange={onManualDiscountPercentageChange}
      />
    </div>
  );
};

// Totais com regra: desconto manual aplicado sobre (subtotal - pointsDiscount)
const Totals = ({ subtotal, pointsDiscount, manualDiscountPercentage, total }) => {
  const safeSubtotal = Math.max(subtotal || 0, 0);
  const safePoints = Math.max(pointsDiscount || 0, 0);
  const baseForManual = Math.max(safeSubtotal - safePoints, 0);
  const unclampedManual = baseForManual * (Math.max(manualDiscountPercentage || 0, 0) / 100);
  const manualDiscountAmount = Math.min(unclampedManual, baseForManual);

  return (
    <div className="space-y-2 text-lg">
      <div className="flex justify-between items-center">
        <span className="text-gray-400">Subtotal</span>
        <span className="font-semibold">R$ {safeSubtotal.toFixed(2).replace('.', ',')}</span>
      </div>

      {safePoints > 0 && (
        <div className="flex justify-between items-center text-green-400 text-base">
          <span>Desconto (Pontos)</span>
          <span className="font-semibold">- R$ {safePoints.toFixed(2).replace('.', ',')}</span>
        </div>
      )}

      {manualDiscountAmount > 0 && (
        <div className="flex justify-between items-center text-green-400 text-base">
          <span>Desconto Manual ({Math.max(manualDiscountPercentage || 0, 0)}%)</span>
          <span className="font-semibold">
            - R$ {manualDiscountAmount.toFixed(2).replace('.', ',')}
          </span>
        </div>
      )}

      <div className="flex justify-between items-center text-2xl font-bold border-t border-gray-600 pt-2 mt-2">
        <span className="text-white">TOTAL</span>
        <span className="text-brand-gold">R$ {(total || 0).toFixed(2).replace('.', ',')}</span>
      </div>
    </div>
  );
};

// Botões de pagamento
const Payment = ({ onFinalizeSale, isProcessing, total, isDisabled }) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const paymentOptions = [
    { id: 'Dinheiro', icon: DollarSign },
    { id: 'Cartão', icon: CreditCard },
    { id: 'Pix', icon: Smartphone },
  ];

  const isButtonDisabled = isProcessing || (total > 0 && !paymentMethod) || isDisabled;

  return (
    <div className="space-y-4 mt-4">
      {total > 0 && (
        <div className="flex justify-around bg-gray-800 p-1 rounded-lg">
          {paymentOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => setPaymentMethod(option.id)}
              className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-md transition-colors text-sm ${
                paymentMethod === option.id
                  ? 'bg-brand-gold text-black font-bold'
                  : 'bg-transparent text-white hover:bg-gray-700'
              }`}
            >
              <option.icon size={16} />
              {option.id}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => onFinalizeSale(paymentMethod)}
        disabled={isButtonDisabled}
        className="w-full flex items-center justify-center p-3 bg-green-600 text-white font-bold rounded-lg transition-all hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed"
      >
        {isProcessing ? <Loader2 className="animate-spin mr-2" /> : <ShoppingCart className="mr-2" />}
        Finalizar Venda
      </button>
    </div>
  );
};

CartPane.Totals = Totals;
CartPane.Payment = Payment;
CartPane.ManualDiscount = ManualDiscount;

export default CartPane;
